import { z } from "zod";

/** Minimum age required to onboard as a freelancer (P.IVA / contractual capacity). */
export const MIN_FREELANCER_AGE = 18;

/**
 * Actionable message shown on the birthdate field when the chosen date would make
 * the user a minor.
 *
 * Kept as a literal here rather than an i18n key because these schemas are built at
 * module scope in `OnboardingContext.tsx`, where `useI18n()` cannot be called. This
 * matches the existing validator convention in `src/libs/validators/currency.ts`.
 * The wording mirrors the backend's own 400 message so FE and BE read identically.
 */
export const MINOR_BIRTHDATE_MESSAGE = "Devi avere almeno 18 anni";

const startOfLocalDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Latest birthdate that still makes someone an adult: the local calendar date
 * exactly `MIN_FREELANCER_AGE` years before `now`, normalised to local midnight.
 *
 * Doubles as the date picker's `maxDate`, so dates that would make the user a minor
 * are not selectable in the first place.
 */
export const maxAdultBirthdate = (now: Date = new Date()): Date =>
  new Date(
    now.getFullYear() - MIN_FREELANCER_AGE,
    now.getMonth(),
    now.getDate(),
  );

/**
 * Calendar view the birthdate picker opens on when the field is empty.
 *
 * This is a *view* anchor only — it never becomes the field's value, so the birthdate
 * stays an explicit user choice (SPEC decision: "default di apertura picker = vista,
 * non valore"). It removes the scroll-back-from-today friction without ever persisting
 * a fabricated date.
 */
export const defaultBirthdateView = (now: Date = new Date()): Date =>
  maxAdultBirthdate(now);

/**
 * True when `birthdate` belongs to someone at least `MIN_FREELANCER_AGE` years old.
 *
 * Compares local calendar days, because the date picker yields local midnight. Turning
 * 18 exactly today counts as adult, matching the backend guard. Note the payload
 * serialises the Date as UTC, which for Italian timezones lands a day *earlier* — i.e.
 * the backend sees the user as marginally older, so a date accepted here can never be
 * rejected there.
 */
export const isAdultBirthdate = (
  birthdate: Date,
  now: Date = new Date(),
): boolean => startOfLocalDay(birthdate) <= maxAdultBirthdate(now);

/** `z.date()` plus the 18+ guard. Still required — an empty field remains invalid. */
export const adultBirthdateSchema = z
  .date()
  .refine((date) => isAdultBirthdate(date), {
    message: MINOR_BIRTHDATE_MESSAGE,
  });
